import React from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsWideScreen } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';

/**
 * Master-detail wide-screen pass (xl+, 2026-09): the record detail that
 * slides in as an overlay Sheet below xl renders as a static, always-visible
 * panel next to the list at >=1280px instead — no navigating away and back
 * to see what's selected. Biggest workflow win on the list pages per the
 * spec that drove this (Estimates, Clients & Leads, Active Jobs).
 *
 * Detail components (JobDetailSheet, ContactProfileSheet, …) wrap their
 * content in this instead of raw <Sheet><SheetContent>; it alone decides
 * which shell fits the viewport. The calling page places the SAME
 * component invocation inside a two-column grid (list | detail, see
 * MasterDetailGrid) that only takes effect at xl+ — in narrow mode the
 * Sheet portals over the whole screen regardless of where it sits in the
 * tree, so nothing needs to change page-side for the existing mobile/
 * tablet behavior.
 *
 * Width: the WIDE panel fills whatever grid column the page gives it (no
 * width class needed here). The NARROW Sheet is `fixed` and needs its own
 * explicit width per caller — pass it via `sheetClassName` (e.g. Jobs'
 * wider tab set wants more room than a Contact profile does).
 *
 * Callers must NOT use SheetTitle/SheetDescription inside `children` —
 * those are Radix Dialog primitives that require Sheet's own Root context
 * and will throw when this renders in inline mode (no Sheet at all). Use
 * `useIsWideScreen()` in the caller to pick a plain heading instead when
 * wide, matching the pattern each detail component already uses.
 *
 * Sticky + height-capped so a tall detail panel scrolls internally next to
 * a long list rather than pushing the page's own scroll past the fold —
 * paired with the ~48px right-edge clearance reserved in DashboardLayout so
 * nothing lands under the Milli dock's collapsed handle. Content that
 * scrolls internally (ScrollArea) should size itself with `flex-1`, not a
 * fixed viewport-relative height — this shell's real height differs
 * between the two modes.
 */
export const ResponsiveDetailPanel = ({
  open,
  onOpenChange,
  className,
  sheetClassName,
  testId,
  children,
}) => {
  const isWide = useIsWideScreen();

  if (isWide) {
    return (
      <div
        className={cn(
          'flex flex-col overflow-hidden rounded-lg border border-border bg-[hsl(var(--card))]',
          'xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)]',
          className
        )}
        data-testid={testId}
      >
        {children}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full flex-col border-l border-border bg-[hsl(var(--card))] p-0',
          sheetClassName
        )}
        data-testid={testId}
      >
        {children}
      </SheetContent>
    </Sheet>
  );
};

export default ResponsiveDetailPanel;
